/**
 * ComplianceTab — Cross-tenant safeguarding & compliance oversight.
 */
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ShieldAlert, AlertTriangle, Clock, Users, FileWarning, Activity } from 'lucide-react';
import { getToken } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RISK_COLORS = {
  low: 'bg-slate-500/20 text-slate-300 border-slate-400/30',
  medium: 'bg-amber-500/20 text-amber-200 border-amber-400/30',
  high: 'bg-orange-500/20 text-orange-200 border-orange-400/30',
  critical: 'bg-rose-500/20 text-rose-200 border-rose-400/30',
};

export const ComplianceTab = () => {
  const [window, setWindow] = useState(60);

  const { data, isLoading } = useQuery({
    queryKey: ['superadmin-compliance', window],
    queryFn: async () => {
      const token = getToken();
      const res = await axios.get(`${API_URL}/api/superadmin/compliance/overview?days=${window}`, { headers: { Authorization: `Bearer ${token}` } });
      return res.data;
    },
    refetchInterval: 60000,
  });

  if (isLoading) return <p className="text-slate-400 p-8">Loading compliance data...</p>;
  const d = data || {};

  return (
    <div className="space-y-6" data-testid="superadmin-compliance-tab">
      <div className="bg-gradient-to-r from-rose-500/10 to-fuchsia-500/10 border-l-4 border-rose-400 p-4 rounded flex items-start gap-3">
        <ShieldAlert className="h-5 w-5 text-rose-300 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-rose-100">
          <p className="font-semibold">Cross-Tenant Compliance Oversight</p>
          <p className="text-rose-200/80 text-xs mt-1">Safeguarding cases, overdue reviews, repeat staff complaints, and behaviour trends across ALL tenants. Access is audit-logged.</p>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <span className="text-sm text-slate-400">Window:</span>
        {[30, 60, 90, 180].map(n => (
          <Button key={n} size="sm" variant={window === n ? 'default' : 'outline'}
                  onClick={() => setWindow(n)}
                  className={window === n ? 'bg-indigo-500 hover:bg-indigo-400 text-white' : 'text-white border-white/20 bg-white/5 hover:bg-white/10'}
                  data-testid={`compliance-window-${n}`}>
            {n}d
          </Button>
        ))}
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPI icon={FileWarning} label="High-Risk Cases" value={d.high_risk_cases_count ?? 0}
             accent="rose" testid="compliance-kpi-high-risk" />
        <KPI icon={Clock} label="Overdue Reviews" value={d.overdue_reviews_count ?? 0}
             accent="amber" testid="compliance-kpi-overdue" />
        <KPI icon={AlertTriangle} label="SG Flagged Open" value={d.safeguarding_flagged_open ?? 0}
             accent="fuchsia" sub={`${d.urgent_open ?? 0} urgent`} testid="compliance-kpi-sg-open" />
        <KPI icon={Activity} label="Sensitive Access (7d)" value={d.sensitive_access_events_7d ?? 0}
             accent="indigo" testid="compliance-kpi-access" />
      </div>

      {/* Behaviour snapshot */}
      <Card className="bg-white/5 backdrop-blur-xl border-white/10 text-white">
        <CardHeader>
          <CardTitle className="text-base flex items-center justify-between">
            <span>Behaviour Activity ({window}d)</span>
            <div className="text-xs flex gap-2">
              <Badge className="bg-slate-500/20 text-slate-200 border-slate-400/30">Total: {d.behaviour?.total ?? 0}</Badge>
              <Badge className="bg-rose-500/20 text-rose-200 border-rose-400/30">Critical: {d.behaviour?.critical ?? 0}</Badge>
              <Badge className="bg-orange-500/20 text-orange-200 border-orange-400/30">Escalated: {d.behaviour?.escalated ?? 0}</Badge>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {(d.behaviour?.top_tenants || []).length === 0 && <p className="text-sm text-slate-500">No incidents in window.</p>}
            {(d.behaviour?.top_tenants || []).map((t, i) => (
              <div key={t.tenant_id} className="flex items-center gap-3 p-2 rounded bg-white/5">
                <span className="text-xs font-mono text-slate-500 w-6">#{i + 1}</span>
                <span className="flex-1 font-semibold text-sm">{t.tenant_name}</span>
                <Badge className="bg-indigo-500/20 text-indigo-200 border-indigo-400/30">{t.total} total</Badge>
                {t.critical > 0 && <Badge className="bg-rose-500/20 text-rose-200 border-rose-400/30">{t.critical} critical</Badge>}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* High-risk cases */}
      <Card className="bg-white/5 backdrop-blur-xl border-white/10 text-white">
        <CardHeader>
          <CardTitle className="text-base">High-Risk Open Cases</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-slate-400 border-b border-white/10">
              <tr>
                <th className="text-left p-3">Case #</th>
                <th className="text-left p-3">Tenant</th>
                <th className="text-left p-3">Title</th>
                <th className="text-left p-3">Risk</th>
                <th className="text-left p-3">Status</th>
                <th className="text-left p-3">Owner</th>
              </tr>
            </thead>
            <tbody>
              {(d.high_risk_cases || []).length === 0 && <tr><td colSpan={6} className="p-5 text-center text-slate-500">No high-risk cases — 🎉</td></tr>}
              {(d.high_risk_cases || []).map(c => (
                <tr key={c.id} className="border-b border-white/5">
                  <td className="p-3 font-mono text-xs">{c.number}</td>
                  <td className="p-3 text-xs text-slate-300">{c.tenant_name}</td>
                  <td className="p-3 font-semibold">{c.title}</td>
                  <td className="p-3"><Badge variant="outline" className={`capitalize ${RISK_COLORS[c.risk_level]}`}>{c.risk_level}</Badge></td>
                  <td className="p-3 capitalize text-xs">{c.status?.replace(/_/g, ' ')}</td>
                  <td className="p-3 text-xs">{c.owner_email || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Overdue reviews */}
      <Card className="bg-white/5 backdrop-blur-xl border-white/10 text-white">
        <CardHeader>
          <CardTitle className="text-base">Overdue Case Reviews</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-slate-400 border-b border-white/10">
              <tr>
                <th className="text-left p-3">Case #</th>
                <th className="text-left p-3">Tenant</th>
                <th className="text-left p-3">Title</th>
                <th className="text-right p-3">Days Overdue</th>
              </tr>
            </thead>
            <tbody>
              {(d.overdue_reviews || []).length === 0 && <tr><td colSpan={4} className="p-5 text-center text-slate-500">No overdue reviews.</td></tr>}
              {(d.overdue_reviews || []).map(c => (
                <tr key={c.id} className="border-b border-white/5">
                  <td className="p-3 font-mono text-xs">{c.number}</td>
                  <td className="p-3 text-xs text-slate-300">{c.tenant_name}</td>
                  <td className="p-3 font-semibold">{c.title}</td>
                  <td className="p-3 text-right">
                    <Badge className={c.days_overdue > 30 ? 'bg-rose-500/20 text-rose-200 border-rose-400/30' : 'bg-amber-500/20 text-amber-200 border-amber-400/30'}>
                      {c.days_overdue}d
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Repeat staff */}
      <Card className="bg-white/5 backdrop-blur-xl border-white/10 text-white">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><Users className="h-4 w-4" /> Repeat Staff Complaints (Cross-Tenant)</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-slate-400 border-b border-white/10">
              <tr>
                <th className="text-left p-3">Staff</th>
                <th className="text-left p-3">Tenant</th>
                <th className="text-right p-3">Complaint Count</th>
              </tr>
            </thead>
            <tbody>
              {(d.repeat_staff || []).length === 0 && <tr><td colSpan={3} className="p-5 text-center text-slate-500">No staff with repeat complaints.</td></tr>}
              {(d.repeat_staff || []).map(s => (
                <tr key={`${s.tenant_id}-${s.staff_id}`} className="border-b border-white/5">
                  <td className="p-3 font-semibold">{s.staff_name}</td>
                  <td className="p-3 text-xs text-slate-300">{s.tenant_name}</td>
                  <td className="p-3 text-right">
                    <Badge className="bg-rose-500/20 text-rose-200 border-rose-400/30">{s.complaint_count}</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
};

const KPI = ({ icon: Icon, label, value, sub, accent, testid }) => {
  const iconBg = {
    rose: 'bg-rose-500/20 border-rose-400/30 text-rose-300',
    amber: 'bg-amber-500/20 border-amber-400/30 text-amber-300',
    fuchsia: 'bg-fuchsia-500/20 border-fuchsia-400/30 text-fuchsia-300',
    indigo: 'bg-indigo-500/20 border-indigo-400/30 text-indigo-300',
  }[accent] || 'bg-slate-500/20 border-slate-400/30 text-slate-300';
  return (
    <Card className="bg-white/5 backdrop-blur-xl border-white/10 text-white" data-testid={testid}>
      <CardContent className="pt-5 pb-4 flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-wider text-slate-400">{label}</p>
          <p className="text-3xl font-bold mt-1">{value}</p>
          {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
        </div>
        <div className={`w-11 h-11 rounded-xl border flex items-center justify-center ${iconBg}`}>
          <Icon className="h-5 w-5" />
        </div>
      </CardContent>
    </Card>
  );
};
